import { useOutletContext } from 'react-router-dom'

import {
  Alert,
  Box,
  Text,
  WizardStepRenderParams,
  useOnNext,
} from '@factorialco/deprecated-design-system'

import DayPicker from 'modules/core/components/DayPicker'
import TextEditor from 'modules/core/components/TextEditor'
import TextField from 'modules/core/components/TextField'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateBackendToUI, dateUIToBackend, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { ClassWizardData } from 'modules/trainings/components/Revamp/NewClassWizard/hooks/useNewClassWizardSteps'
import { validateCodigo } from 'modules/trainings/lib/fundaeUtils'
import type { TrainingsTraining } from 'modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/types'

export const BasicInformationStep = ({
  data,
  onDataChanged,
}: WizardStepRenderParams<ClassWizardData>) => {
  const { training } = useOutletContext<{ training: TrainingsTraining }>()

  useOnNext(() => {
    track(trackingEvents.training.trainingGroupCreationWizard.goToDetailsNextStep)
  })
  return (
    <Box gap="s32" paddingX="s16">
      <Box gap="s16">
        <Box gap="s8">
          <Text size="400" weight="semibold" color="grey1000">
            {i18n.t(
              'trainings.trainings_revamp.training_class.class_wizard.basic_information_step.reviewed_copies.title'
            )}
          </Text>
        </Box>
        <TextField
          value={data.name}
          label={i18n.t(
            'trainings.trainings_revamp.training_class.class_wizard.basic_information_step.group_name'
          )}
          onChange={(value) => onDataChanged({ ...data, name: value })}
          mandatory
        />
      </Box>
      <Box flexDirection="row" gap="s16">
        <DayPicker
          value={data.startDate ? dateBackendToUI(data.startDate) : undefined}
          mode="single"
          label={i18n.t(
            'trainings.trainings_revamp.training_class.class_wizard.basic_information_step.start_date'
          )}
          onChange={(value) => onDataChanged({ ...data, startDate: dateUIToBackend(value) })}
          mandatory
          maxDate={data.endDate ? stringToDate(data.endDate, 'backend') : undefined}
        />
        <DayPicker
          value={data.endDate ? dateBackendToUI(data.endDate) : undefined}
          mode="single"
          label={i18n.t(
            'trainings.trainings_revamp.training_class.class_wizard.basic_information_step.end_date'
          )}
          onChange={(value) => onDataChanged({ ...data, endDate: dateUIToBackend(value) })}
          mandatory
          minDate={data.startDate ? stringToDate(data.startDate, 'backend') : undefined}
        />
      </Box>

      {Boolean(training?.fundaeSubsidized) && (
        <Box gap="s8">
          <TextField
            description={i18n.t(
              'trainings.trainings_revamp.training_class.class_wizard.basic_information_step.fundae_configuration.description'
            )}
            error={
              validateCodigo(data.codigoGrupo)
                ? undefined
                : i18n.t(
                    'trainings.trainings_revamp.training_class.class_wizard.basic_information_step.fundae_configuration.error'
                  )
            }
            label={i18n.t(
              'trainings.trainings_revamp.training_class.class_wizard.basic_information_step.fundae_configuration.label'
            )}
            max={99999}
            maxLength={5}
            min={1}
            onChange={(value) => onDataChanged({ ...data, codigoGrupo: value })}
            placeholder="00001"
            type="number"
            value={data.codigoGrupo}
          />
          <Alert color="info">
            <Text.Normal>
              {i18n.t(
                'trainings.trainings_revamp.training_class.class_wizard.basic_information_step.fundae_configuration.alert'
              )}
            </Text.Normal>
          </Alert>
        </Box>
      )}
      <Box gap="s8">
        <Text size="200" weight="semibold">
          {i18n.t(
            'trainings.trainings_revamp.training_class.class_wizard.basic_information_step.description_title'
          )}
        </Text>
        <TextEditor
          placeholder=""
          initialEditorState={{
            data: data.description || '',
          }}
          onChange={(value) => onDataChanged({ ...data, description: value ? String(value) : '' })}
        />
      </Box>
    </Box>
  )
}
